import React from "react";
import { Target, RotateCw } from "lucide-react";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import PremiumEventCard from "../components/ui/PremiumEventCard";
import { DartsEvent } from "../types";
import { useEvents, useRefresh } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import { splitEventsByTimeline } from "../utils/eventSections";

interface DashboardViewProps {
  onEventClick: (event: DartsEvent) => void;
  onNotificationClick: () => void;
}

const DashboardView: React.FC<DashboardViewProps> = ({ onEventClick }) => {
  const { events, isLoading, error, fetchEvents } = useEvents();
  const { mypage, refresh: refreshMypage } = useMypageContext();
  const { isRefreshing, refreshImmediate } = useRefresh({
    showErrorToast: true,
    showSuccessToast: false,
  });

  const { upNext, schedule, pastEvents } = splitEventsByTimeline(events);

  const handleRefresh = async () => {
    await refreshImmediate(fetchEvents, refreshMypage);
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <div className="overflow-y-auto scrollbar-hide flex-1">
        <div className="flex items-center justify-between pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 px-6 sticky top-0 bg-[#f8fafc]/80 backdrop-blur-xl z-20 border-b border-slate-200/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-gradient-to-tr from-red-500 to-rose-500 flex items-center justify-center shadow-md rotate-3">
              <Target
                className="w-[18px] h-[18px] text-white -rotate-3"
                strokeWidth={2.5}
              />
            </div>
            <h1 className="text-xl font-black tracking-tighter uppercase bg-gradient-to-r from-red-500 to-rose-500 bg-clip-text text-transparent">
              {UI_TEXT.common.appName}
            </h1>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="group inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100 active:bg-slate-200 active:scale-95 active:shadow-inner transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            title="データを更新"
          >
            <RotateCw
              className={`block w-5 h-5 text-slate-600 transition-transform duration-150 group-active:scale-90 group-active:-rotate-12 ${isRefreshing ? "animate-spin" : ""}`}
              strokeWidth={2}
            />
          </button>
        </div>
        <div className="px-6 py-6 pb-40 text-left">
          <MembershipStatsCard
            points={mypage?.yearly_point ?? "-"}
            rank={mypage?.yearly_rank ?? "-"}
            dartsliveName={mypage?.dartslive_card_name}
            eventGroupName={mypage?.event_group?.name}
            theme="red"
          />
          {(isLoading || isRefreshing) && (
            <p className="text-slate-400 text-xs font-bold text-center py-8">
              {UI_TEXT.common.loading}
            </p>
          )}
          {error && (
            <p className="text-rose-500 text-xs font-bold text-center py-8">
              {error}
            </p>
          )}
          {!isLoading && !isRefreshing && !error && upNext && (
            <>
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.upNext}
              </h2>
              <PremiumEventCard
                event={upNext}
                isPrimary
                theme="red"
                onClick={onEventClick}
              />
            </>
          )}
          {!isLoading && !isRefreshing && !error && schedule.length > 0 && (
            <>
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.schedule}
              </h2>
              {schedule.map((ev) => (
                <PremiumEventCard
                  key={ev.id}
                  event={ev}
                  isPrimary={false}
                  theme="red"
                  onClick={onEventClick}
                />
              ))}
            </>
          )}
          {!isLoading && !isRefreshing && !error && pastEvents.length > 0 && (
            <>
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.pastEvents}
              </h2>
              {pastEvents.map((ev) => (
                <PremiumEventCard
                  key={ev.id}
                  event={ev}
                  isPrimary={false}
                  isPast
                  theme="red"
                  onClick={onEventClick}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
